import {getGarrisonType} from "./dashboard-filters.js";

const hasValue = value => value !== null && value !== undefined && value !== "";

const addAction = (actions, action) => {
    actions.push({
        variant: "primary",
        menuVariant: action.variant,
        ...action,
    });
};

const getBoName = configuration => configuration?.boName || "BO";

const buildAttendanceActions = (attendance, {permissions, handlers}) => {
    if (!permissions.attendance) return [];

    const actions = [];
    const attendanceId = attendance.attendanceId;
    const attendanceStatus = attendance.attendanceType ?? "onHold";

    if (hasValue(attendance.latitude) && permissions.gis) {
        addAction(actions, {
            key: "attendance-map",
            label: "Mapa",
            onClick: () => handlers.openGis?.(`attendance-${attendanceId}`),
        });
    }

    if (["onHold", "delayed", "schedule"].includes(attendanceStatus) && permissions.task) {
        addAction(actions, {
            key: "attendance-to-task",
            label: "Tarefa",
            onClick: () => handlers.convertAttendanceToTask?.(attendance),
        });
    }

    addAction(actions, {
        key: "attendance-close",
        label: "Encerrar",
        variant: "danger",
        onClick: () => handlers.closeAttendance?.(attendance),
    });

    return actions;
};

const buildTaskActions = (task, {permissions, handlers}) => {
    if (!permissions.task) return [];

    const actions = [];
    const hasAssignment = task.hasAssignment === true || task.assignment === true || hasValue(task.garrisonId);
    const dispatchId = task.dispatchId ?? null;

    if (
        permissions.attendance
        && task.source === "ATTENDANCE"
        && !hasAssignment
        && !hasValue(dispatchId)
    ) {
        addAction(actions, {
            key: "task-to-attendance",
            label: "Atendimento",
            onClick: () => handlers.moveTaskToAttendance?.(task),
        });
    }

    addAction(actions, {
        key: "task-close",
        label: "Encerrar",
        variant: "danger",
        onClick: () => handlers.closeTask?.(task),
    });

    return actions;
};

const buildGarrisonActions = (garrison, {configuration, permissions, handlers}) => {
    if (!permissions.garrison) return [];

    const actions = [];
    const boName = getBoName(configuration);
    const garrisonId = garrison.garrisonId;
    const isPanic = garrison.isPanic === true;
    const hasMobile = garrison.garrisonHasMobile === true;
    const byMobileNotRead = garrison.createdByMobile === true && !hasValue(garrison.byMobileAckDt);
    const dispatchStatus = garrison.dispatchStatus === true || hasValue(garrison.dispatchId);
    const dispatchActivity = garrison.dispatchActivity === true || getGarrisonType(garrison) === "dispatchActivity";
    const dispatchType = dispatchStatus ? getGarrisonType(garrison) : null;
    const isRefuellingOrMaintenance = garrison.isReffulingOrMaintenance === true || garrison.isRefuellingOrMaintenance === true;
    const canShowDispatchCreationActions = !dispatchStatus || (permissions.garrison && dispatchActivity && !isRefuellingOrMaintenance);

    if (isPanic) {
        addAction(actions, {
            key: "garrison-ack-panic",
            label: "Desativar pânico",
            className: "btn-ackpanic",
            onClick: () => handlers.ackPanic?.(garrison),
        });
    }

    if (byMobileNotRead) {
        addAction(actions, {
            key: "garrison-ack-mobile",
            label: "Ciente",
            onClick: () => handlers.ackGarrisonByMobile?.(garrison),
        });
    }

    if (garrison.garrisonHasAssignment === true) {
        addAction(actions, {
            key: "garrison-assignments",
            label: "Atribuições",
            className: "btn-assignment",
            onClick: () => handlers.openGarrisonAssignments?.(garrison),
        });
    }

    if (canShowDispatchCreationActions) {
        if (garrison.garrisonHasSchedule === true) {
            addAction(actions, {
                key: "garrison-schedule",
                label: "Atividades programadas",
                className: "btn-setdispatch",
                onClick: () => handlers.openGarrisonSchedule?.(garrison),
            });
        }

        addAction(actions, {
            key: "garrison-dispatch",
            label: "Despacho",
            className: "btn-setdispatch",
            onClick: () => handlers.openGarrisonDispatch?.(garrison),
        });

        addAction(actions, {
            key: "garrison-activity",
            label: "Atividade",
            className: "btn-setdispatch",
            onClick: () => handlers.openGarrisonActivity?.(garrison),
        });

        addAction(actions, {
            key: "garrison-support",
            label: "Apoio",
            className: "btn-setdispatch",
            onClick: () => handlers.openGarrisonSupport?.(garrison),
        });
    }

    if (dispatchStatus && dispatchType === "dispatchSupport" && permissions.garrison) {
        addAction(actions, {
            key: "garrison-change-owner",
            label: "Mudar para principal",
            className: "btn-setdispatch",
            onClick: () => handlers.changeOwner?.(garrison),
        });
    }

    if (dispatchStatus && permissions.garrison) {
        addAction(actions, {
            key: "garrison-close-dispatch",
            label: "Encerrar",
            variant: "danger",
            onClick: () => handlers.closeDispatch?.(garrison),
        });
    } else if (!dispatchStatus) {
        addAction(actions, {
            key: "garrison-change-contingents",
            label: "Alterar contingentes",
            variant: "warning",
            onClick: () => handlers.changeContingents?.(garrison),
        });
        addAction(actions, {
            key: "garrison-close",
            label: "Encerrar",
            variant: "danger",
            onClick: () => handlers.closeGarrison?.(garrison),
        });
    }

    addAction(actions, {
        key: "garrison-details",
        label: "Informações (I)",
        onClick: () => handlers.openGarrisonInformation?.(garrison),
    });

    addAction(actions, {
        key: "garrison-history",
        label: "Histórico (H)",
        onClick: () => handlers.openGarrisonHistory?.(garrison),
    });

    if (hasMobile && permissions.gis) {
        addAction(actions, {
            key: "garrison-map",
            label: "Mapa (M)",
            onClick: () => handlers.openGis?.(`garrison-${garrisonId}`),
        });
    }

    if (
        dispatchStatus
        && permissions.garrison
        && garrison.dispatchOccurrence !== "FORBIDDEN"
        && !hasValue(garrison.occurrenceNumber)
        && !dispatchActivity
    ) {
        addAction(actions, {
            key: "garrison-generate-bo",
            label: `Gerar ${boName}`,
            variant: "warning",
            onClick: () => handlers.generateBo?.(garrison),
        });
    }

    if (!isPanic && hasMobile) {
        addAction(actions, {
            key: "garrison-panic",
            label: "Pânico",
            variant: "danger",
            onClick: () => handlers.setPanic?.(garrison),
        });
    }

    if (hasValue(garrison.vehicleCameraRtsp)) {
        addAction(actions, {
            key: "garrison-camera",
            label: "Câmera (V)",
            onClick: () => handlers.openMosaic?.(garrison.vehicleCameraRtsp),
        });
    }

    return actions;
};

export const buildDashboardCardActions = (card, context) => {
    if (!card) return [];

    if (card.type === "attendance") return buildAttendanceActions(card.item, context);
    if (card.type === "task") return buildTaskActions(card.item, context);
    if (card.type === "garrison") return buildGarrisonActions(card.item, context);

    return [];
};
